import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap, Sparkles, Bug, Wrench, Gift, ChevronRight, Bell, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";

const updates = [
  {
    version: "2.4.0",
    date: "March 2026",
    isNew: true,
    title: "Help & Support Ticketing",
    description: "Learners can now create support tickets, track their status, and communicate with support team directly within the platform.",
    type: "feature",
    items: ["Create and track support tickets", "Real-time reply thread", "Priority levels and categories", "FAQ quick help section"],
  },
  {
    version: "2.3.0",
    date: "March 2026",
    isNew: true,
    title: "Enterprise Certificate Design",
    description: "Completely redesigned certificates with professional layout, business + Odesie dual branding, gold accents, and verification IDs.",
    type: "feature",
    items: ["Dual logo support (Business + Odesie)", "View certificates inline", "Download as PDF", "Verification ID & URL"],
  },
  {
    version: "2.2.0",
    date: "March 2026",
    isNew: false,
    title: "Learner Dashboard Overhaul",
    description: "Completely rebuilt learner experience with real-time stats, compliance badges, learning paths, announcements, and quick navigation.",
    type: "feature",
    items: ["Dashboard with stats and quick links", "Learning paths with course sequence view", "Course catalog with request access", "Compliance tracking with progress bars"],
  },
  {
    version: "2.1.0",
    date: "February 2026",
    isNew: false,
    title: "Cross-Platform Sharing",
    description: "Share courses, assessments, compliance templates, and reports with team members across the organization.",
    type: "feature",
    items: ["Share any resource via dialog", "User search with autocomplete", "Copy link functionality", "Custom share messages"],
  },
  {
    version: "2.0.0",
    date: "February 2026",
    isNew: false,
    title: "Performance & Scalability",
    description: "Major backend refactoring from monolith to modular architecture for better performance and maintainability.",
    type: "improvement",
    items: ["Modular route architecture", "Faster API responses", "Improved error handling", "Better database queries"],
  },
];

const typeIcons = {
  feature: { icon: Sparkles, color: "text-emerald-600", bg: "bg-emerald-50", label: "New Feature" },
  improvement: { icon: Wrench, color: "text-blue-600", bg: "bg-blue-50", label: "Improvement" },
  fix: { icon: Bug, color: "text-amber-600", bg: "bg-amber-50", label: "Bug Fix" },
};

const ProductUpdatesPage = () => {
  const { user } = useAuth();
  const isLearner = user?.role === "learner";
  const variant = isLearner ? "learner" : user?.role === "tenant_admin" ? "tenant_admin" : "super_admin";

  return (
    <AdminLayout title="Product Updates" variant={variant}>
      <div className="space-y-6 max-w-3xl" data-testid="product-updates-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
            Product Updates
          </h1>
          <p className="text-muted-foreground text-sm">Latest features, improvements, and fixes</p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-border" />

          <div className="space-y-6">
            {updates.map((update, i) => {
              const typeConfig = typeIcons[update.type] || typeIcons.feature;
              const TypeIcon = typeConfig.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="relative pl-12"
                >
                  {/* Timeline dot */}
                  <div className={`absolute left-2.5 top-4 w-[14px] h-[14px] rounded-full border-2 border-background ${update.isNew ? "bg-primary" : "bg-muted-foreground/30"}`} />

                  <Card className={update.isNew ? "border-primary/20 shadow-sm" : ""} data-testid={`update-${i}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className={`${typeConfig.bg} ${typeConfig.color} text-[10px]`}>
                            <TypeIcon className="w-3 h-3 mr-1" /> {typeConfig.label}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">v{update.version}</Badge>
                          {update.isNew && <Badge className="bg-emerald-500 text-white text-[10px]">New</Badge>}
                        </div>
                        <span className="text-xs text-muted-foreground">{update.date}</span>
                      </div>
                      <h3 className="font-semibold text-base mb-1">{update.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{update.description}</p>
                      <ul className="space-y-1">
                        {update.items.map((item, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm">
                            <ChevronRight className="w-3 h-3 text-primary shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProductUpdatesPage;
